// Shared logic for scaling the number of concurrent /api/query/* requests down as a
// workload grows, protecting Query API from sustained saturation under large loads.

export const DEFAULT_QUERY_CONCURRENCY = 10;
export const MIN_QUERY_CONCURRENCY = 5;
export const QUERY_CONCURRENCY_STEP_SIZE = 60;

/**
 * Scale a concurrency limit down from a ceiling toward {@link MIN_QUERY_CONCURRENCY} as the workload
 * grows: `max(ceiling − floor(workload / stepSize), MIN)`.
 *
 * @param workload the size of the workload driving the scaling (dashboard cards, metric-tree nodes,
 *        or live queue depth).
 * @param options.ceiling the concurrency at zero workload (the effective maximum), defaulting to
 *        {@link DEFAULT_QUERY_CONCURRENCY}. The query-permit broker passes a higher ceiling that
 *        folds its pad into the burst cap.
 * @param options.stepSize how many units of workload reduce the concurrency by 1, defaulting to
 *        {@link QUERY_CONCURRENCY_STEP_SIZE}.
 * @returns the concurrency limit, in [{@link MIN_QUERY_CONCURRENCY}, ceiling]
 */
export function scaleQueryConcurrency(
  workload: number,
  {
    ceiling = DEFAULT_QUERY_CONCURRENCY,
    stepSize = QUERY_CONCURRENCY_STEP_SIZE,
  }: {ceiling?: number; stepSize?: number} = {},
): number {
  return Math.max(ceiling - Math.floor(workload / stepSize), MIN_QUERY_CONCURRENCY);
}
